import React from 'react';
import { motion } from 'framer-motion';
import { Monitor, Smartphone, Cpu, Cloud, PenTool, Database, Zap } from 'lucide-react';

const services = [
  {
    title: 'Web Application Development',
    description: 'We construct robust, high-performance web systems tailored to streamline your business operations and capture new market opportunities.',
    icon: <Monitor size={32} />,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50'
  },
  {
    title: 'Mobile App Development',
    description: 'Expand your business reach and engage clients on the go with high-performance native and cross-platform mobile apps built to scale.',
    icon: <Smartphone size={32} />,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-50'
  },
  {
    title: 'Custom Software Solutions',
    description: 'Bespoke technology engineered to eliminate operational bottlenecks, solve specific business challenges, and integrate workflows seamlessly.',
    icon: <Database size={32} />,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50'
  },
  {
    title: 'AI & Decision Systems',
    description: 'Deploy intelligence to automate costly manual processes, extract deep operational insights, and unlock predictive capabilities for growth.',
    icon: <Cpu size={32} />,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50'
  },
  {
    title: 'Cloud Engineering & Scaling',
    description: 'Establish resilient, cost-effective cloud networks that ensure your business applications remain secure, fast, and available 24/7.',
    icon: <Cloud size={32} />,
    color: 'text-sky-600',
    bgColor: 'bg-sky-50'
  },
  {
    title: 'Business-Centric Product Design',
    description: 'Craft frictionless digital journeys designed to convert visitors, maximize user retention, and turn complex workflows into simple tasks.',
    icon: <PenTool size={32} />,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50'
  },
  {
    title: 'Process & Workflow Automation',
    description: 'Connect tools and automate repetitive tasks to reduce overhead, eliminate human errors, and free your team to focus on strategic growth.',
    icon: <Zap size={32} />,
    color: 'text-rose-600',
    bgColor: 'bg-rose-50'
  }
];

const Services = () => {
  return (
    <section id="services" className="py-24 bg-transparent relative">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-slate-900 mb-6"
          >
            Solutions to <span className="text-blue-600">Grow Your Business</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-slate-600"
          >
            We design and engineer tailored technology solutions specifically built to overcome your unique challenges and drive measurable business growth.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ y: -5 }}
              className="p-8 rounded-3xl border border-slate-100 bg-white shadow-sm hover:shadow-xl hover:shadow-blue-900/5 transition-all group"
            >
              <div className={`w-16 h-16 rounded-2xl ${service.bgColor} ${service.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                {service.icon}
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">{service.title}</h3>
              <p className="text-slate-600 font-light leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
